// Corner floral arrangement used as decorative chrome. Renders either a `src`
// image (when a PNG asset is dropped in /public/floral-corner-{tl,tr,bl,br}.png)
// OR an inline SVG composition. The inline version approximates the photo:
// cream garden roses built from Bézier-curve petals + sage eucalyptus leaves
// with veins + baby's-breath cluster dots + curved stems.
//
// Pure chrome — pointer-events-none, mix-blend-multiply for the "stamped on
// cream paper" feel.
//
// When a PNG `src` is provided the component emits a <picture> that prefers a
// same-basename .webp (built via scripts/optimize-floral-corners.mjs) and
// falls back to the PNG for older browsers / missing-webp runtime cases.
const POSITION_CLASSES = {
  tl: 'top-0 left-0',
  tr: 'top-0 right-0 rotate-90',
  bl: 'bottom-0 left-0 -rotate-90',
  br: 'bottom-0 right-0 rotate-180',
};

function InlineFlorals() {
  return (
    <svg
      viewBox="0 0 400 400"
      xmlns="http://www.w3.org/2000/svg"
      className="block h-auto w-full"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="fc-rose" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#F7F4ED" />
          <stop offset="55%" stopColor="#EDE2D8" />
          <stop offset="92%" stopColor="#D8C2B0" />
          <stop offset="100%" stopColor="#A98B73" stopOpacity="0.8" />
        </radialGradient>
        <radialGradient id="fc-roseDeep" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#F7F4ED" />
          <stop offset="40%" stopColor="#D8C2B0" />
          <stop offset="80%" stopColor="#A98B73" />
          <stop offset="100%" stopColor="#6B5240" stopOpacity="0.7" />
        </radialGradient>
        <radialGradient id="fc-roseGold" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#F4E8C9" />
          <stop offset="60%" stopColor="#C9A24A" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#8C6F3F" stopOpacity="0.85" />
        </radialGradient>
        <linearGradient id="fc-leaf" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A6B292" />
          <stop offset="55%" stopColor="#6F7E5E" />
          <stop offset="100%" stopColor="#3F4A36" />
        </linearGradient>
        <linearGradient id="fc-leafPale" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#C4D0AE" />
          <stop offset="100%" stopColor="#7E9070" />
        </linearGradient>

        {/* Symbol: a 6-petal cream rose with Bézier petals + stamen */}
        <symbol id="fc-rose-60" viewBox="-30 -30 60 60">
          {[0, 60, 120, 180, 240, 300].map((deg) => (
            <path
              key={`outer-${deg}`}
              d="M 0 -24 C 14 -18 14 -6 0 0 C -14 -6 -14 -18 0 -24 Z"
              fill="url(#fc-roseDeep)"
              transform={`rotate(${deg})`}
              opacity="0.92"
            />
          ))}
          {[30, 90, 150, 210, 270, 330].map((deg) => (
            <path
              key={`inner-${deg}`}
              d="M 0 -16 C 9 -11 9 -4 0 0 C -9 -4 -9 -11 0 -16 Z"
              fill="url(#fc-rose)"
              transform={`rotate(${deg})`}
              opacity="0.97"
            />
          ))}
          <circle r="6" fill="url(#fc-roseGold)" />
          <circle r="2" fill="#8C6F3F" opacity="0.95" />
        </symbol>

        {/* Symbol: eucalyptus leaf with central vein */}
        <symbol id="fc-euca" viewBox="0 -14 70 28">
          <path
            d="M 0 0 Q 25 -14 60 0 Q 25 14 0 0 Z"
            fill="url(#fc-leaf)"
          />
          <path
            d="M 4 -1 Q 28 -2 56 -1"
            stroke="#3F4A36"
            strokeWidth="0.7"
            fill="none"
            opacity="0.55"
          />
        </symbol>
        <symbol id="fc-euca-pale" viewBox="0 -14 70 28">
          <path d="M 0 0 Q 25 -12 56 0 Q 25 12 0 0 Z" fill="url(#fc-leafPale)" />
          <path
            d="M 4 -1 Q 25 -2 52 -1"
            stroke="#5E6F50"
            strokeWidth="0.6"
            fill="none"
            opacity="0.5"
          />
        </symbol>

        {/* Symbol: baby's-breath cluster — 5 tiny 5-petal blossoms */}
        <symbol id="fc-gyp" viewBox="-12 -12 24 24">
          <g fill="#F7F4ED" opacity="0.95">
            {[
              [0, 0],
              [5, -3],
              [-5, -3],
              [3, 4],
              [-3, 4],
            ].map(([cx, cy], i) => (
              <g key={i} transform={`translate(${cx} ${cy})`}>
                {Array.from({ length: 5 }).map((_, j) => (
                  <ellipse
                    key={j}
                    cx="0"
                    cy="-2.4"
                    rx="1.6"
                    ry="2.6"
                    transform={`rotate(${j * 72})`}
                  />
                ))}
                <circle r="0.8" fill="#E8DCC2" />
              </g>
            ))}
          </g>
        </symbol>
      </defs>

      {/* Stems — curved paths drawn first so flowers/leaves sit on top */}
      <g
        stroke="#6F7E5E"
        strokeWidth="1.4"
        fill="none"
        opacity="0.6"
        strokeLinecap="round"
      >
        <path d="M 220 -10 Q 200 60 160 110 T 100 220" />
        <path d="M -10 240 Q 60 250 130 250" />
        <path d="M 60 -10 Q 90 50 120 100" strokeWidth="1.2" />
        <path d="M 200 200 Q 240 230 280 270" strokeWidth="1.2" />
      </g>

      {/* Eucalyptus leaves fanning from the corner */}
      <use href="#fc-euca" x="0" y="0" width="60" height="22" transform="rotate(-22 60 80)" />
      <use href="#fc-euca" x="0" y="0" width="64" height="22" transform="rotate(-12 100 110)" />
      <use href="#fc-euca" x="0" y="0" width="58" height="22" transform="rotate(18 80 140)" />
      <use href="#fc-euca" x="0" y="0" width="68" height="22" transform="rotate(35 150 170)" />
      <use href="#fc-euca" x="0" y="0" width="56" height="22" transform="rotate(38 50 200)" />
      <use href="#fc-euca-pale" x="0" y="0" width="50" height="20" transform="rotate(-30 130 220)" />
      <use href="#fc-euca" x="0" y="0" width="50" height="22" transform="rotate(-48 200 220)" />
      <use href="#fc-euca-pale" x="0" y="0" width="46" height="20" transform="rotate(10 240 90)" />

      {/* Roses — large cream + medium mustard-touched + bud */}
      <use href="#fc-rose-60" x="0" y="0" width="100" height="100" transform="translate(20 0)" />
      <use href="#fc-rose-60" x="0" y="0" width="78" height="78" transform="translate(140 6)" />
      <use href="#fc-rose-60" x="0" y="0" width="114" height="114" transform="translate(0 122)" />
      <use href="#fc-rose-60" x="0" y="0" width="64" height="64" transform="translate(216 200)" />

      {/* Baby's breath — clusters scattered along the spray */}
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(214 90)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(226 102)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(204 110)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(174 70)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(86 238)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(100 252)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(72 252)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(118 50)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(242 160)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(234 190)" />
      <use href="#fc-gyp" x="0" y="0" width="20" height="20" transform="translate(264 230)" />

      {/* Gold specks scattered in the leaves */}
      <g fill="#B89A6B">
        <circle cx="104" cy="92" r="1.6" />
        <circle cx="174" cy="54" r="1.4" />
        <circle cx="62" cy="150" r="1.4" />
        <circle cx="214" cy="184" r="1.4" />
        <circle cx="44" cy="234" r="1.4" />
      </g>
    </svg>
  );
}

// Photographic assets are pre-composed for their target corner — do NOT
// rotate them. The rotations in POSITION_CLASSES are only correct for the
// SVG fallback, which has a single TL-biased composition reused at all
// four corners.
const POSITION_CLASSES_NO_ROTATE = {
  tl: 'top-0 left-0',
  tr: 'top-0 right-0',
  bl: 'bottom-0 left-0',
  br: 'bottom-0 right-0',
};

export default function FloralCorner({ position = 'tl', src }) {
  const svgWrapperClasses = `pointer-events-none absolute z-20 select-none mix-blend-multiply ${
    POSITION_CLASSES[position] ?? POSITION_CLASSES.tl
  }`;

  if (src) {
    const positionClasses =
      POSITION_CLASSES_NO_ROTATE[position] ?? POSITION_CLASSES_NO_ROTATE.tl;
    // Auto-pair a .webp alongside a .png src — modern browsers take the WebP,
    // older ones fall back to the PNG. If the WebP is missing at runtime the
    // <img src> below still works (e.g. /superior.png loads directly).
    const webpSrc = src.endsWith('.png') ? src.replace(/\.png$/, '.webp') : null;
    return (
      <picture
        className={`pointer-events-none absolute z-10 ${positionClasses}`}
      >
        {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
        <img
          src={src}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="pointer-events-none h-auto w-[240px] select-none sm:w-[340px] md:w-[420px] lg:w-[480px] xl:w-[540px]"
        />
      </picture>
    );
  }

  return (
    <div aria-hidden="true" className={svgWrapperClasses} style={{ width: 'min(480px, 62%)' }}>
      <InlineFlorals />
    </div>
  );
}
